import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import SlidePanel from '../SlidePanel';
import PeoplePicker, { Person } from './PeoplePicker';

interface PersonRef {
  account_id: string;
  display_name: string | null;
}

interface ProjectRow {
  project_key: string;
  name: string | null;
  owner: PersonRef | null;
  involved: PersonRef[];
  teams: Array<{ id: number; name: string }>;
}

const SHOWN_INVOLVED = 3;

// Settings → Projects: who owns each project and who is involved. They see the project and its updates on
// their Home. Which teams work on a project is read here and changed under Manage teams.
export default function ProjectsTab() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const [owner, setOwner] = useState<string[]>([]);
  const [involved, setInvolved] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [roles, users] = await Promise.all([apiService.getProjectRoles(), apiService.getJiraUsers()]);
      if (!roles.success) throw new Error(roles.error || 'Could not load the projects');
      setRows(roles.data ?? []);
      setPeople(users.data?.success ? users.data.data ?? [] : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load the projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.project_key.toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q)) : rows;
  }, [rows, query]);

  const open = (row: ProjectRow) => {
    setEditing(row);
    setOwner(row.owner ? [row.owner.account_id] : []);
    setInvolved(row.involved.map((p) => p.account_id));
  };

  const knownNames = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const r of rows) for (const p of [...(r.owner ? [r.owner] : []), ...r.involved]) out[p.account_id] = p.display_name;
    return out;
  }, [rows]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await apiService.setProjectRoles(editing.project_key, owner[0] ?? null, involved.filter((a) => a !== owner[0]));
      if (!r.success) throw new Error(r.error || 'Could not save');
      showToast('success', `Saved the people on ${editing.project_key}`);
      setEditing(null);
      await load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save the people on this project');
    } finally {
      setSaving(false);
    }
  };

  const names = (list: PersonRef[]) => list.map((p) => p.display_name ?? p.account_id);

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-gray-700">
        Choose who owns each project and who is involved. They see the project and its updates on their Home. The teams working on a project are set under{' '}
        <Link to="/settings?tab=manage-teams" className="hit-area-sm font-medium text-blue-700 underline-offset-2 hover:underline">
          Manage teams
        </Link>
        .
      </p>

      <div className="relative max-w-sm">
        <label htmlFor="projects-search" className="sr-only">
          Search projects
        </label>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
        <input
          id="projects-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search projects"
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">
          <span>{error}</span>
          <button type="button" onClick={load} className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 hover:bg-rose-100">
            Try again
          </button>
        </div>
      )}

      {/* relative: the visually hidden header text is absolutely placed, and must stay inside the scrolling box */}
      <div className="relative overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">
              <th scope="col" className="sticky left-0 bg-white px-4 py-3">Project</th>
              <th scope="col" className="px-4 py-3">Owner</th>
              <th scope="col" className="px-4 py-3">Involved</th>
              <th scope="col" className="px-4 py-3">Teams</th>
              <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600" role="status">
                  Loading the projects…
                </td>
              </tr>
            ) : shown.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600">
                  {rows.length === 0 ? 'There are no projects yet. They appear after the first sync from Jira.' : 'No project matches that.'}
                </td>
              </tr>
            ) : (
              shown.map((r) => (
                <tr key={r.project_key} className="align-top">
                  <th scope="row" className="sticky left-0 bg-white px-4 py-3 text-left font-normal">
                    <span className="font-medium text-gray-900">{r.project_key}</span>
                    {r.name && <span className="block text-gray-600">{r.name}</span>}
                  </th>
                  <td className="px-4 py-3 text-gray-900">{r.owner ? r.owner.display_name ?? r.owner.account_id : <span className="text-gray-600">No owner yet</span>}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {r.involved.length === 0 ? (
                      <span className="text-gray-600">Nobody yet</span>
                    ) : (
                      <>
                        {names(r.involved).slice(0, SHOWN_INVOLVED).join(', ')}
                        {r.involved.length > SHOWN_INVOLVED && <span className="text-gray-600"> and {r.involved.length - SHOWN_INVOLVED} more</span>}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-900">{r.teams.length === 0 ? <span className="text-gray-600">None</span> : r.teams.map((t) => t.name).join(', ')}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => open(r)}
                      aria-label={`Edit the people on ${r.project_key}`}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SlidePanel isOpen={!!editing} onClose={() => setEditing(null)} title={editing ? `People on ${editing.project_key}` : 'People'} widthPercent={32} minWidthPx={380}>
        {editing && (
          <div className="space-y-6 p-1">
            {editing.name && <p className="text-sm text-gray-700">{editing.name}</p>}
            <PeoplePicker
              label="Owner"
              people={people}
              value={owner}
              onChange={(next) => {
                setOwner(next);
                // The owner is not also listed as involved.
                setInvolved((cur) => cur.filter((a) => a !== next[0]));
              }}
              single
              knownNames={knownNames}
              emptyText="No owner yet. The owner answers for the project."
            />
            <PeoplePicker
              label="Involved"
              people={people}
              value={involved}
              onChange={setInvolved}
              exclude={owner}
              knownNames={knownNames}
              emptyText="Nobody yet. Involved people are kept informed."
            />
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
